// @flow
import * as React from 'react';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import { Container } from 'reactstrap';
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import SelfCheckInHeader from 'components/atoms/SelfCheckInHeader/';
import ContactPreference from 'components/molecules/ContactPreference';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Constants
import {
  SCI_SERVICES_ROUTE,
  SCI_SIGNATURE_ROUTE,
  WAITING_OPTIONS,
  SELF_CHECK_IN_FLOW
} from 'constants/selfCheckIn';
import { NAVIGATION } from 'constants/navigations';
import { labelsStepIndicatorSCI } from 'constants/CTAConfig';

// Types
import type { ContactInfo } from 'api/xws/login/entities/ContactInfo';

// Utils
import { isPhoneNumberValid } from 'utils/string';

import './AdvisorAssistance.scss';

const ns = 'advisor-assistance';
const { FORWARD } = NAVIGATION;
const { IN_WAITING_AREA_ON_TRANSPORT, CALL, TEXT } = WAITING_OPTIONS;
const {
  NON_OPERATING_HOURS_ADDITIONAL_SERVICE_CHECK_IN,
  NON_OPERATING_HOURS_CHECK_IN
} = SELF_CHECK_IN_FLOW;

export type Props = {
  intl: IntlShape,
  setWaitState: string => void,
  goTo: (path: string, direction: string) => void,
  transportationLabel: string,
  waitState: string,
  dealerCountryCode: string,
  contactInfo: ContactInfo,
  selfCheckInFlow: string,
  gtmContactPreferenceMethod: (eventResult: string) => void,
  showSCIProgressBar: boolean
};

class AdvisorAssistance extends React.Component<Props> {
  componentDidMount() {
    const { setWaitState, selfCheckInFlow, waitState } = this.props;
    const isPhoneContactWaitState = waitState === TEXT || waitState === CALL;
    if (
      !isPhoneContactWaitState &&
      (selfCheckInFlow === NON_OPERATING_HOURS_ADDITIONAL_SERVICE_CHECK_IN ||
        selfCheckInFlow === NON_OPERATING_HOURS_CHECK_IN)
    ) {
      setWaitState(CALL);
    }
  }

  render() {
    const {
      goTo,
      intl,
      setWaitState,
      transportationLabel,
      contactInfo,
      dealerCountryCode,
      waitState,
      selfCheckInFlow,
      gtmContactPreferenceMethod,
      showSCIProgressBar
    } = this.props;

    const isDealerClosedNow =
      selfCheckInFlow === NON_OPERATING_HOURS_ADDITIONAL_SERVICE_CHECK_IN ||
      selfCheckInFlow === NON_OPERATING_HOURS_CHECK_IN;
    // ToDo: remove hardcoded strings and defaultMessages once localization strings are ready and services integrated
    const waitingArea = intl.formatMessage({
      id: 'c8.self.check.in.config.transportation.waiting.area',
      defaultMessage: 'Waiting area'
    });
    let titleId = 'c8.self.check.in.advisor.page.complete.and.wait';
    let titleDefaultMessage =
      'Complete check in and wait in the {waitingArea} to speak to an advisor';
    let buttonTextId = 'c8.self.check.in.common.ok';
    if (isDealerClosedNow) {
      titleId = 'c8.self.check.in.advisor.page.extended.hours.title';
      titleDefaultMessage =
        'A service advisor will contact you the next business day…';
      buttonTextId = 'c8.self.check.in.footer.next';
    }
    const loanerCar = transportationLabel;
    const mobilePhone = contactInfo ? contactInfo.mobilePhone : '';
    const isValid =
      !isDealerClosedNow ||
      ((waitState === CALL || waitState === TEXT) &&
        isPhoneNumberValid(mobilePhone, dealerCountryCode));

    return (
      <AppointmentFlowWizard
        isSelfCheckIn={true}
        className={ns}
        showSCIProgressBar={showSCIProgressBar}
        header={() =>
          showSCIProgressBar ? (
            <CtaStepIndicator
              htmlId="VehiclePageStepIndicator"
              labels={labelsStepIndicatorSCI(intl)}
              activeStep={3}
            />
          ) : (
            <SelfCheckInHeader
              label={intl.formatMessage({
                id: 'c8.self.check.in.header.services'
              })}
              route={SCI_SERVICES_ROUTE}
              ariaLabel={intl.formatMessage({
                id: 'c8.aria.back.to.services',
                defaultMessage: 'Back to services'
              })}
            />
          )
        }
        footer={() => (
          <AppointmentFlowFooter
            buttonDisabled={!isValid}
            buttonText={intl.formatMessage({
              id: buttonTextId
            })}
            onClick={() => {
              !isDealerClosedNow && setWaitState(IN_WAITING_AREA_ON_TRANSPORT);
              goTo(SCI_SIGNATURE_ROUTE, FORWARD);
              isDealerClosedNow && gtmContactPreferenceMethod(waitState);
            }}
          />
        )}
        body={() => (
          <React.Fragment>
            {showSCIProgressBar && (
              <SelfCheckInHeader
                label={intl.formatMessage({
                  id: 'c8.self.check.in.header.services'
                })}
                route={SCI_SERVICES_ROUTE}
                ariaLabel={intl.formatMessage({
                  id: 'c8.aria.back.to.services',
                  defaultMessage: 'Back to services'
                })}
              />
            )}
            <Container className={`${ns}__container`}>
              {!isDealerClosedNow && (
                <p className={`${ns}__label`}>
                  {intl.formatMessage(
                    {
                      id:
                        'c8.self.check.in.advisor.page.you.indicated.loaner.vehicle',
                      defaultMessage:
                        'You indicated that you need a {loanerCar}'
                    },
                    {
                      loanerCar
                    }
                  )}
                </p>
              )}
              <h1 className={`${ns}__msg`}>
                {intl.formatMessage(
                  {
                    id: titleId,
                    defaultMessage: titleDefaultMessage
                  },
                  {
                    waitingArea
                  }
                )}
              </h1>
              {isDealerClosedNow && (
                <ContactPreference
                  selectedOptionLabel={waitState.toLowerCase()}
                  error={!isValid}
                />
              )}
            </Container>
          </React.Fragment>
        )}
      />
    );
  }
}

export default AdvisorAssistance;



// WEBPACK FOOTER //
// ./src/components/pages/SelfCheckIn/SpeakToAdvisorPage/AdvisorAssistance/AdvisorAssistance.js